import { forwardRef, ReactNode } from "react";
import { ButtonHTMLAttributes } from "react";
import { Button } from '~/design-system/elements/Button/Button';
import { cn } from "~/lib/utils";
import { SubscriptionTier } from "~/queries/subscription";
import { askOutOfCredits } from "./outOfCredits";

interface UpgradeButtonUIProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  size?: "xs" | "sm" | "default" | "lg";
  className?: string;
  children?: ReactNode;
  currentPlan?: SubscriptionTier | null;
  onUpgradeClick?: () => void;
  onSeePlans?: () => void;
}

export const UpgradeButtonUI = forwardRef<HTMLButtonElement, UpgradeButtonUIProps>(
  ({
    size = "sm",
    className,
    children = "Upgrade",
    currentPlan,
    onUpgradeClick,
    onSeePlans,
    ...props
  }, ref) => {
    if (!currentPlan || currentPlan === SubscriptionTier.MAX) {
      return null;
    }

    // The dialog is awaited rather than mounted, so `onUpgradeClick` fires FIRST — it is the
    // "they pressed Upgrade" signal and it used to run alongside the open, not after the answer.
    const handleClick = async () => {
      onUpgradeClick?.();
      if (await askOutOfCredits(currentPlan)) {
        onSeePlans?.();
      }
    };

    return (
      <Button
        ref={ref}
        variant="upgrade"
        size={size}
        leadIcon="upgrade"
        className={cn(className)}
        onClick={handleClick}
        {...props}
      >
        {children}
      </Button>
    );
  }
);

UpgradeButtonUI.displayName = "UpgradeButtonUI";
